import * as React from "react";

import { AIChatContext } from "@/Chat/ChatSidebar/AIChatContext";
import { ContextType } from "@/Chat/ChatSidebar/contextType";

/**
 * Provides a minimal, read-only AIChatContext for rendering ChatByIdView
 * outside of the normal chat sidebar (e.g. in admin views).
 *
 * All mutation handlers are no-ops and interactive features are disabled.
 */
export const ReadOnlyAIChatProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const noop = React.useCallback(() => {}, []);

  const value = React.useMemo(
    () => ({
      isFullscreen: false,
      showHistory: false,
      autoNavEnabled: false,
      currentChatId: undefined,
      context: { dataset_ids: [], file_ids: [] },
      contextDisplayNames: { datasets: {}, files: {} },
      modelProfile: undefined,

      setIsFullscreen: noop as (value: boolean) => void,
      setShowHistory: noop as (value: boolean) => void,
      setAutoNavEnabled: noop as (value: boolean) => void,
      setCurrentChatId: noop as (chatId: string | undefined) => void,
      setModelProfile: noop as (profile: string) => void,

      handleContextDelete: noop as (
        contextType: ContextType,
        contextContent: string,
      ) => void,
      handleNewChat: noop,
      handleToggleFullscreen: noop,
      handleToggleHistory: noop,
      handleToggleAutoNav: noop,
      handleHistoryChatClicked: noop as (chatId: string) => void,
      handleLinkClick: noop,
      handleResetUIState: noop,
    }),
    [noop],
  );

  return (
    <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>
  );
};
